import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { useToken } from '../../hooks/useToken';
import { PageCenteredLayout } from '../layout/PageCenteredLayout';
import LoadingIndicator from '../common/LoadingIndicator';
import {
  fetchSolutionDeployment,
  selectSolutionDeployment,
  selectSolutionDeploymentsStatus,
} from '../../reducers/solutionDeploymentsSlice';
import { fetchSnowflakeAccounts, selectAccounts } from '../../reducers/snowflakeAccountsSlice';
import { fetchSolution } from '../../reducers/solutionsSlice';
import StatusPill from '../common/StatusPill';
import Button from '../controls/Button';
import SectionLayout from '../layout/SectionLayout';
import Grid3Layout from '../layout/Grid3Layout';
import CustomTooltip from '../common/CustomTooltip';
import { useSolution } from '../../hooks/useSolution';
import { ArrowTopRightOnSquareIcon, CircleStackIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { type ISolutionDeploymentDetail } from '../../interfaces/ISolutionDeployment';
import ProjectIcon from '../common/ProjectIcon';
import CopyUrlButton from '../controls/CopyUrlButton';
import { getConfig } from '../../config/config-helper';
import {
  getDeploymentStatusColorVariant,
  getPipelineStatusColorVariant,
} from '../../utilities/solutionDeploymentUtils';
import NewHomepageLaunchButton from '../controls/NewHomepageLaunchButton';
import UpdateSolutionDeploymentButton from '../controls/UpdateSolutionDeploymentButton';
import DeleteSolutionDeploymentButton from '../controls/DeleteSolutionDeploymentButton';
import RemoveFromHistoryButton from '../controls/RemoveFromHistoryButton';
import { useNavigationTab } from '../../hooks/useNavigationTab';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { getSolutionPath } from '../../utilities/solutionUtils';

export default function SolutionDemoDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const token = useToken();
  const deploymentId = id ? parseInt(id, 10) : undefined;
  const deployment = useAppSelector((state) => selectSolutionDeployment(state, deploymentId));
  const status = useAppSelector(selectSolutionDeploymentsStatus);
  const snowflakeAccounts = useAppSelector(selectAccounts);
  const { dataopsliveBaseUrl } = getConfig();

  const solutionId = deployment?.project_id.toString() ?? '';
  const { solution } = useSolution(solutionId);

  useEffect(() => {
    if (token && id) {
      dispatch(fetchSolutionDeployment({ token, deploymentId: parseInt(id, 10) })).catch((err) => {
        console.error('Failed to fetch solution deployment:', err);
      });
      dispatch(fetchSnowflakeAccounts(token)).catch((err) => {
        console.error('Failed to fetch snowflake accounts:', err);
      });
    }
  }, [token, id, dispatch]);

  // Poll for status updates when deployment is being deleted
  useEffect(() => {
    if (!token || !id || deployment?.status !== 'deleting') return;

    const interval = setInterval(() => {
      dispatch(fetchSolutionDeployment({ token, deploymentId: parseInt(id, 10) })).catch((err) => {
        console.error('Failed to poll deployment status:', err);
      });
    }, 10000); // Poll every 10 seconds

    return () => {
      clearInterval(interval);
    };
  }, [token, id, dispatch, deployment?.status]);

  // Fetch solution details if not already loaded
  useEffect(() => {
    if (solutionId && solution?.listing?.name === '') {
      dispatch(fetchSolution({ token, fullPath: `/solutions/${solutionId}`, iid: solutionId })).catch((err) => {
        console.log('Fetch solution error:', err);
      });
    }
  }, [solution?.listing?.name, dispatch, token, solutionId]);

  const getAccountName = (accountId: number) => {
    const account = snowflakeAccounts.find((acc) => acc.id === accountId);
    return account?.account ?? `Account ID: ${accountId}`;
  };

  // Add navigation tab for this deployment
  useNavigationTab(
    deployment && solution?.name
      ? {
          section: 'discover/new-deployments',
          label: `${deployment.solution_prefix} / ${solution.name} / ${getAccountName(
            deployment.snowflake_account_id,
          )}`,
          href: `/deployments/${deployment.id}`,
          status: status === 'loading' ? 'loading' : 'loaded',
        }
      : null,
    [deployment?.id, deployment?.solution_prefix, solution?.name, deployment?.snowflake_account_id, status],
  );

  const getSnowflakeAccountURL = (accountId: number): string => {
    const account = snowflakeAccounts.find((acc) => acc.id === accountId);
    if (account?.account) {
      return `https://${account.account}.snowflakecomputing.com/`;
    }
    return 'https://www.snowflake.com/login/';
  };

  // Get the pipeline URL
  const getPipelineUrl = (pipelineId: number): string => {
    if (!solution?.fullPath) {
      return '#';
    }
    return `${dataopsliveBaseUrl}/${solution.fullPath}/-/pipelines/${pipelineId}`;
  };

  if (!deployment && status === 'loading') {
    return (
      <PageCenteredLayout>
        <LoadingIndicator selectRequestStatus={() => status} />
      </PageCenteredLayout>
    );
  }

  if (!deployment) {
    return (
      <PageCenteredLayout>
        <div className="text-center py-12">
          <p className="text-dataops-primary-dark-blue/60 mb-4">Deployment not found</p>
          <Link to="/solution-demos" className="text-dataops-primary-light-blue hover:underline">
            Back to Solution Demos
          </Link>
        </div>
      </PageCenteredLayout>
    );
  }

  // Get the latest pipeline run
  const latestPipelineRun =
    deployment.pipeline_runs.length > 0
      ? [...deployment.pipeline_runs].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )[0]
      : null;

  return (
    <div className="relative">
      <LoadingIndicator selectRequestStatus={() => status} />
      <main>
        <header className="relative isolate pt-10">
          <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute left-16 top-full -mt-1 transform-gpu opacity-50 blur-3xl xl:left-1/2 xl:-ml-80">
              <div
                style={{
                  clipPath:
                    'polygon(100% 38.5%, 82.6% 100%, 60.2% 37.7%, 52.4% 32.1%, 47.5% 41.8%, 0.2% 65.6%, 27.5% 23.4%, 0.1% 35.3%, 17.9% 0%, 27.7% 23.4%, 76.2% 2.5%, 74.2% 56%, 100% 38.5%)',
                }}
                className="aspect-[1154/678] w-[62.125rem] bg-gradient-to-br from-dataops-primary-light-blue to-dataops-secondary-dark-blue"
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 h-px bg-gray-900/5" />
          </div>

          <PageCenteredLayout>
            <div className="mx-auto flex items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none">
              <div className="flex items-center gap-x-6">
                <ProjectIcon iconSrc={solution?.avatarUrl ?? solution?.listing?.icon} />
                <h1>
                  <div className="mt-1 flex space-x-2 h-12 items-center">
                    <span className="text-base font-semibold text-dataops-primary-dark-blue">
                      {deployment.solution_prefix}
                    </span>{' '}
                    <span>/</span>
                    <Link
                      to={getSolutionPath(solutionId, solution)}
                      className="text-dataops-primary-dark-blue/70 hover:underline"
                    >
                      <span>{solution.name}</span>
                    </Link>
                    {/* {updateAvailable && <Tag color={'orange'}>Update available</Tag>} */}
                  </div>
                </h1>
              </div>
              <div className="flex items-center sm:gap-x-1">
                <CopyUrlButton view="deployment" />
                {/* <ShareWithCustomerButton solution={solution} deployment={deployment} /> */}
              </div>
            </div>
          </PageCenteredLayout>
        </header>

        <PageCenteredLayout>
          <Grid3Layout>
            {/* Details Section - Compact */}
            <SectionLayout label="Details" className="md:col-span-1 md:row-start-1">
              <div className="space-y-4">
                <InfoField label="Snowflake Account" value={getAccountName(deployment.snowflake_account_id)} />
                <InfoField label="Git Reference" value={deployment.git_ref} />
                <InfoField label="Pipeline Filename" value={deployment.pipeline_filename} />
              </div>
            </SectionLayout>

            {/* Status Section */}
            <SectionLayout label="Status" className="md:col-span-1 md:row-start-1">
              {latestPipelineRun ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dataops-primary-dark-blue/80 py-2">Deployment</span>
                    <StatusPill
                      status={deployment.status}
                      colorVariant={getDeploymentStatusColorVariant(deployment.status)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-dataops-primary-dark-blue/80 py-2">Pipeline</span>
                    <StatusPill
                      status={latestPipelineRun.status}
                      colorVariant={getPipelineStatusColorVariant(latestPipelineRun.status)}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-dataops-primary-dark-blue/80">Last Updated</div>
                    <div className="text-sm text-dataops-primary-dark-blue">
                      {latestPipelineRun.last_updated_at
                        ? new Date(latestPipelineRun.last_updated_at).toLocaleString()
                        : 'N/A'}
                    </div>
                  </div>
                  <a
                    href={getPipelineUrl(latestPipelineRun.pipeline_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-dataops-primary-light-blue hover:underline inline-flex items-center"
                  >
                    View latest pipeline
                    <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1" />
                  </a>
                </div>
              ) : (
                <div className="text-sm text-dataops-primary-dark-blue/60">No pipeline runs yet</div>
              )}
            </SectionLayout>

            {/* Actions Section - Compact */}
            <SectionLayout
              label="Actions"
              className="bg-dataops-supporting-gray/40 md:col-span-1 md:col-start-3 md:row-start-1"
            >
              <div className="space-y-1">
                {/* Update Button */}
                {deploymentId && (
                  <UpdateSolutionDeploymentButton
                    deploymentId={deploymentId}
                    disabled={
                      status === 'loading' ||
                      deployment.status === 'creating' ||
                      deployment.status === 'updating' ||
                      deployment.status === 'deleting'
                    }
                  />
                )}

                {/* Homepage Launch Button */}
                <NewHomepageLaunchButton
                  deployment={deployment}
                  projectPath={solution?.fullPath}
                  size="large"
                  primaryButton={false}
                />

                <CustomTooltip tooltip="Coming soon">
                  <div>
                    <Button intent="light" size="large" disabled={true} className="w-full">
                      <BeakerIcon className="w-4 h-4 mr-1" />
                      Run Validation
                    </Button>
                  </div>
                </CustomTooltip>

                {/* Functional Snowsight Button */}
                <Button
                  url={getSnowflakeAccountURL(deployment.snowflake_account_id)}
                  intent="light"
                  size="large"
                  className="w-full"
                >
                  <CircleStackIcon className="w-4 h-4 mr-1" />
                  Open Snowsight UI
                  <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-1" />
                </Button>

                {/* Delete button - hidden when already deleted */}
                {deploymentId && deployment.status !== 'deleted' && (
                  <DeleteSolutionDeploymentButton
                    deploymentId={deploymentId}
                    disabled={
                      status === 'loading' || deployment.status === 'deleting' || deployment.status === 'updating'
                    }
                  />
                )}

                {/* Remove from history button - shown when deployment is deleted */}
                {deploymentId && deployment.status === 'deleted' && (
                  <RemoveFromHistoryButton deploymentId={deploymentId} />
                )}
              </div>
            </SectionLayout>

            {/* Configuration Section */}
            {Object.keys(deployment.extra_env_vars).length > 0 && (
              <SectionLayout label="Configuration" className="md:col-span-3">
                <div className="flex flex-col h-full space-y-4">
                  <div className="space-y-6">
                    <DeploymentConfig
                      deployment={deployment}
                      configKey="DATAOPS_CATALOG_CUSTOMER_NAME"
                      label="Customer name"
                    />
                    <DeploymentConfigLogo deployment={deployment} />
                    <DeploymentConfig
                      deployment={deployment}
                      configKey="DATAOPS_CATALOG_OPPORTUNITY_ID"
                      label="Salesforce ID"
                    />
                    <ExtraEnvVarsSection deployment={deployment} />
                  </div>
                </div>
              </SectionLayout>
            )}
          </Grid3Layout>
        </PageCenteredLayout>
      </main>
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-dataops-primary-dark-blue/80">{label}</div>
      <div className="text-dataops-primary-dark-blue">{value}</div>
    </div>
  );
}

function DeploymentConfig({
  deployment,
  configKey,
  label,
}: {
  deployment: ISolutionDeploymentDetail;
  configKey: string;
  label?: string;
}) {
  const configValue = deployment.extra_env_vars[configKey] ?? '';
  return (
    <div className="space-y-1">
      <div className="text-xs text-dataops-primary-dark-blue/80">{label ?? configKey}</div>
      <div>{configValue !== '' ? configValue : 'Not available.'}</div>
    </div>
  );
}

function DeploymentConfigLogo({ deployment }: { deployment: ISolutionDeploymentDetail }) {
  const logoUrl = deployment.extra_env_vars.DATAOPS_CATALOG_CUSTOMER_LOGO_URL ?? '';
  // parse a URL safe URL back into a URL
  const decodedUrl = decodeURIComponent(logoUrl);
  return (
    <div className="space-y-1">
      <div className="text-xs text-dataops-primary-dark-blue/80">Customer logo</div>
      <div>
        {logoUrl !== '' ? (
          <img
            src={decodedUrl}
            alt="Customer logo"
            className="h-16 max-w-full p-1 rounded-md mr-2 outline outline-1 outline-dataops-supporting-gray"
          />
        ) : (
          'Not available.'
        )}
      </div>
    </div>
  );
}

function ExtraEnvVarsSection({ deployment }: { deployment: ISolutionDeploymentDetail }) {
  // Filter out the known/displayed env vars
  const knownKeys = [
    'DATAOPS_CATALOG_CUSTOMER_NAME',
    'DATAOPS_CATALOG_OPPORTUNITY_ID',
    'DATAOPS_CATALOG_CUSTOMER_LOGO_URL',
  ];

  const extraVars = Object.entries(deployment.extra_env_vars).filter(([key]) => !knownKeys.includes(key));

  if (extraVars.length === 0) {
    return null;
  }

  return (
    <Disclosure>
      {({ open }) => (
        <>
          <DisclosureButton className="flex w-full justify-between rounded-lg bg-gray-100 px-4 py-2 text-left text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
            <span>Extra Environment Variables ({extraVars.length})</span>
            <ChevronDownIcon className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-gray-500`} />
          </DisclosureButton>
          <DisclosurePanel className="px-4 pt-4 pb-2">
            <div className="space-y-2">
              {extraVars.map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <div className="text-xs text-dataops-primary-dark-blue/80">{key}</div>
                  <div className="font-mono text-sm text-gray-700">{value}</div>
                </div>
              ))}
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}
